import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, CalendarDays, Clock, AlertTriangle, User, Flag } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { useTasks } from "@/hooks/useTasks";
import { TaskCard } from "./TaskCard";
import { TaskModal } from "./TaskModal";
import { TaskDetail } from "./TaskDetail";
import { TASK_ASSIGNEES, TASK_PRIORITIES, TASK_STATUSES, TASK_WORKSTREAMS, statusAccent, isOverdue, taskMentions, ukToday, ukAddDays } from "@/data/taskOptions";

const today = () => ukToday();
const inNext7 = (d) => { if (!d) return false; const t = today(); const w = ukAddDays(7); return d >= t && d <= w; };
const PRANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const KANBAN = ["To do", "In progress", "Complete"];

const SummaryCard = ({ testid, icon: Icon, label, value, tone }) => (
  <div data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-[#E4E9F3] bg-white p-4">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ backgroundColor: tone }}><Icon size={18} /></span>
    <div><p className="font-numeric text-2xl font-bold leading-none text-[#14245F]">{value}</p><p className="mt-1 text-xs font-semibold text-[#5A6B8C]">{label}</p></div>
  </div>
);

const F = ({ testid, v, on, label, options }) => (
  <Select value={v} onValueChange={on}>
    <SelectTrigger data-testid={testid} className="w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent><SelectItem value="all">{label}: All</SelectItem>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

export const TasksPanel = ({ openTaskId, onTaskConsumed, initialView, initialAssignee, onNavConsumed }) => {
  const { canWrite, currentUser } = useUser();
  const T = useTasks();
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState("today");
  const [q, setQ] = useState("");
  const [assignF, setAssignF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [wsF, setWsF] = useState("all");
  const [milestoneF, setMilestoneF] = useState("all");
  const [overdueF, setOverdueF] = useState("all");
  const [sort, setSort] = useState("due");
  const [myDay, setMyDay] = useState(false);
  const [flaggedForMe, setFlaggedForMe] = useState(false);
  const [includeShared, setIncludeShared] = useState(true);
  const myName = currentUser?.name;
  const isFounder = myName === "Steven" || myName === "Richard";

  const loadSummary = () => api.tasksSummary().then(setSummary).catch(() => {});
  useEffect(() => { loadSummary(); }, [T.tasks]);

  // Deep-link: /?tab=tasks&task=<id> opens that task's detail (or an unavailable toast).
  useEffect(() => {
    if (!openTaskId || T.loading) return;
    const found = T.tasks.find((t) => t.id === openTaskId);
    if (found) { setView("all"); T.setDetail(found); }
    else toast.error("That task is unavailable — it may have been deleted.");
    onTaskConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId, T.loading]);

  // Deep-link from a founder board's "View all overdue": preset view + founder filter.
  useEffect(() => {
    if (!initialView && !initialAssignee) return;
    if (initialView) setView(initialView);
    if (initialAssignee) setAssignF(initialAssignee);
    onNavConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialView, initialAssignee]);

  const base = useMemo(() => {
    let rows = T.tasks.filter((t) => {
      if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
      if (assignF !== "all") {
        if (assignF === "Both") { if (t.assigned_to !== "Both") return false; }
        else if (!(t.assigned_to === assignF || (includeShared && t.assigned_to === "Both"))) return false;
      }
      if (statusF !== "all" && t.status !== statusF) return false;
      if (prioF !== "all" && t.priority !== prioF) return false;
      if (wsF !== "all" && t.workstream !== wsF) return false;
      if (milestoneF !== "all" && t.related_milestone_id !== milestoneF) return false;
      if (overdueF === "overdue" && !isOverdue(t)) return false;
      if (myDay && isFounder && !(t.assigned_to === myName || t.assigned_to === "Both")) return false;
      if (flaggedForMe && isFounder && !taskMentions(t).includes(myName)) return false;
      return true;
    });
    const sorters = {
      due: (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"),
      priority: (a, b) => (PRANK[b.priority] || 0) - (PRANK[a.priority] || 0),
      assignee: (a, b) => (a.assigned_to || "").localeCompare(b.assigned_to || ""),
      status: (a, b) => (a.status || "").localeCompare(b.status || ""),
      updated: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
    };
    return [...rows].sort(sorters[sort] || sorters.due);
  }, [T.tasks, q, assignF, statusF, prioF, wsF, milestoneF, overdueF, sort, myDay, isFounder, myName, flaggedForMe, includeShared]);

  const viewRows = useMemo(() => {
    const d = today();
    if (view === "today") return base.filter((t) => t.status !== "Cancelled" && (t.task_date === d || t.due_date === d));
    if (view === "upcoming") return base.filter((t) => t.status !== "Cancelled" && inNext7(t.due_date));
    if (view === "overdue") return base.filter((t) => isOverdue(t));
    return base; // all / kanban
  }, [base, view]);

  const cardProps = (t) => ({ task: t, relatedNames: T.relatedNames, canWrite, onOpen: T.setDetail, onStart: T.start, onComplete: T.complete, onReopen: T.reopen });
  const milestoneOpts = (T.relations["launch-milestones"] || []).map((m) => ({ value: m.id, label: m.title }));

  const onDrop = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const t = T.tasks.find((x) => x.id === id);
    if (t && canWrite && t.status !== status) T.transition(t, status, `Task moved to ${status}`);
  };

  return (
    <div className="space-y-6" data-testid="tasks-panel">
      <PageHeader eyebrow="Command Centre" title="Tasks" description="Plan and drive daily execution across every workstream." actions={canWrite && <Button data-testid="tasks-add-btn" onClick={T.openAdd} className="rounded-full bg-[#06B6D4] text-white hover:bg-[#0891B2]"><Plus size={16} /> Add Task</Button>} />

      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-testid="tasks-summary-cards">
          <SummaryCard testid="sum-due-today" icon={CalendarDays} label="Due today" value={summary.due_today} tone="#1D4ED8" />
          <SummaryCard testid="sum-completed-today" icon={Clock} label="Completed today" value={summary.completed_today} tone="#16A34A" />
          <SummaryCard testid="sum-overdue" icon={AlertTriangle} label="Overdue" value={summary.overdue} tone="#FB6F5A" />
          <SummaryCard testid="sum-steven" icon={User} label="Steven open" value={summary.steven_open} tone="#0EA5A4" />
          <SummaryCard testid="sum-richard" icon={User} label="Richard open" value={summary.richard_open} tone="#E0A82E" />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList data-testid="tasks-view-tabs" className="mb-5 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          {["today", "upcoming", "overdue", "all", "kanban"].map((x) => (
            <TabsTrigger key={x} value={x} data-testid={`tasks-tab-${x}`} className="rounded-full px-4 py-1.5 text-sm font-semibold capitalize text-[#5A6B8C] data-[state=active]:bg-[#06B6D4] data-[state=active]:text-white">{x === "all" ? "All Tasks" : x === "upcoming" ? "Upcoming · next 7 days" : x}</TabsTrigger>
          ))}
        </TabsList>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-[#E4E9F3] bg-white p-1" data-testid="tasks-assignee-toggle">
            {[{ v: "all", l: "All" }, { v: "Steven", l: "Steven" }, { v: "Richard", l: "Richard" }, { v: "Both", l: "Shared" }].map((o) => (
              <button
                key={o.v}
                data-testid={`assignee-toggle-${o.v.toLowerCase()}`}
                onClick={() => setAssignF(o.v)}
                className={`rounded-full px-3.5 py-1 text-sm font-semibold transition-colors ${assignF === o.v ? "bg-[#1D4ED8] text-white" : "text-[#5A6B8C] hover:text-[#14245F]"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {(assignF === "Steven" || assignF === "Richard") && (
            <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]" data-testid="tasks-include-shared-label">
              <Switch data-testid="tasks-include-shared" checked={includeShared} onCheckedChange={setIncludeShared} />
              Include shared
            </label>
          )}
        </div>

        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
          <div className="relative flex-1 min-w-[180px]"><Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]" /><Input data-testid="tasks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="rounded-full border-[#E4E9F3] bg-white pl-9" /></div>
          <div className="flex flex-wrap items-center gap-2">
            {isFounder && (
              <button
                data-testid="tasks-my-day"
                onClick={() => { const next = !myDay; setMyDay(next); if (next) setView("today"); }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${myDay ? "border-transparent bg-[#1D4ED8] text-white" : "border-[#1D4ED8]/30 text-[#1D4ED8] hover:bg-[#1D4ED8]/5"}`}
              >
                <User size={14} /> My day
              </button>
            )}
            {isFounder && (
              <button
                data-testid="tasks-flagged-me"
                onClick={() => setFlaggedForMe((s) => !s)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${flaggedForMe ? "border-transparent bg-[#E0A82E] text-white" : "border-[#E0A82E]/40 text-[#B8791A] hover:bg-[#E0A82E]/5"}`}
              >
                <Flag size={14} /> Flagged for me
              </button>
            )}
            <F testid="tasks-filter-assignee" v={assignF} on={setAssignF} label="Founder" options={TASK_ASSIGNEES} />
            <F testid="tasks-filter-status" v={statusF} on={setStatusF} label="Status" options={TASK_STATUSES} />
            <F testid="tasks-filter-priority" v={prioF} on={setPrioF} label="Priority" options={TASK_PRIORITIES} />
            <F testid="tasks-filter-workstream" v={wsF} on={setWsF} label="Workstream" options={TASK_WORKSTREAMS} />
            <F testid="tasks-filter-milestone" v={milestoneF} on={setMilestoneF} label="Milestone" options={milestoneOpts} />
            <F testid="tasks-filter-overdue" v={overdueF} on={setOverdueF} label="Overdue" options={[{ value: "overdue", label: "Overdue only" }]} />
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="tasks-sort" className="w-auto min-w-[130px] rounded-full border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="due">Sort: Due date</SelectItem>
                <SelectItem value="priority">Sort: Priority</SelectItem>
                <SelectItem value="assignee">Sort: Founder</SelectItem>
                <SelectItem value="status">Sort: Status</SelectItem>
                <SelectItem value="updated">Sort: Last updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {["today", "upcoming", "overdue", "all"].map((x) => (
          <TabsContent key={x} value={x}>
            {viewRows.length === 0 ? (
              <p data-testid="tasks-empty" className="py-12 text-center text-sm text-[#5A6B8C]">No tasks here.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid={`tasks-grid-${x}`}>
                {viewRows.map((t) => <TaskCard key={t.id} {...cardProps(t)} />)}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="kanban">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="tasks-kanban">
            {KANBAN.map((col) => {
              const items = base.filter((t) => t.status === col);
              return (
                <div key={col} data-testid={`kanban-col-${col.replace(/\s/g, "-").toLowerCase()}`} onDragOver={(e) => canWrite && e.preventDefault()} onDrop={(e) => onDrop(e, col)} className="flex flex-col rounded-2xl border border-[#E4E9F3] bg-[#F7F9FE]/60 p-2.5">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: statusAccent(col) }}><span className="h-2 w-2 rounded-full" style={{ backgroundColor: statusAccent(col) }} /> {col}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#5A6B8C]">{items.length}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {items.length === 0 ? <p className="px-1 py-6 text-center text-xs text-[#9DAAC6]">Empty</p> :
                      items.map((t) => <TaskCard key={t.id} {...cardProps(t)} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <TaskModal open={T.modalOpen} onOpenChange={T.setModalOpen} initial={T.editing} relations={T.relations} onSubmit={T.save} />
      <TaskDetail task={T.detail} relations={T.relations} relatedNames={T.relatedNames} canWrite={canWrite}
        onOpenChange={(o) => !o && T.setDetail(null)} onEdit={T.openEdit} onStart={T.start} onComplete={T.complete}
        onReopen={T.reopen} onCancel={T.cancel} onDelete={T.setConfirmDelete} onReschedule={T.reschedule} onDuplicate={T.duplicate} onComment={T.comment} onOpenRelated={T.openRelated} />
      <ConfirmDialog open={!!T.confirmDelete} onOpenChange={(o) => !o && T.setConfirmDelete(null)} title="Delete this task?" softDelete onConfirm={(reason) => T.remove(T.confirmDelete, reason)} />
    </div>
  );
};
